import axiosInstance from "src/Funcs/axiosFuncs/axiosInstance";
import { createUploadStallGuard } from "src/Funcs/APIFuncs/uploadStallGuard";
import store from "src/Redux/Store";
import {
  getSocketUserChat,
  disconnectSocketUserChat,
  emitWithCallback,
  emitWhenConnected,
} from "src/Funcs/APIFuncs/socketUserChat";
import { registerGroupSocketListeners } from "src/Redux/Users/GroupChat/groupChatActions";
import React from "react";
import { toast } from "react-toastify";
import ChatToast from "src/Components/Chat/ChatToast";
import { getToastNavigate } from "src/Funcs/toastNavigate";
import {
  CHAT_GET_USER_CHATS_REQUEST,
  CHAT_GET_USER_CHATS_SUCCESS,
  CHAT_GET_USER_CHATS_FAIL,
  CHAT_GET_CHAT_BY_ID_REQUEST,
  CHAT_GET_CHAT_BY_ID_SUCCESS,
  CHAT_GET_CHAT_BY_ID_FAIL,
  CHAT_SEND_MESSAGE_REQUEST,
  CHAT_SEND_MESSAGE_SUCCESS,
  CHAT_SEND_MESSAGE_FAIL,
  CHAT_VOICE_UPLOAD_COMPLETE,
  CHAT_EDIT_MESSAGE_SUCCESS,
  CHAT_DELETE_MESSAGE_SUCCESS,
  CHAT_BULK_DELETE_MESSAGES_SUCCESS,
  CHAT_NEW_MESSAGE,
  CHAT_NEW_CHAT_REQUEST,
  CHAT_REQUEST_ACCEPTED,
  CHAT_REQUEST_REJECTED,
  CHAT_MESSAGE_LIKED,
  CHAT_MESSAGE_REACTION,
  CHAT_REMOTE_SEEN,
  CHAT_SELECT_CHAT,
  CHAT_LEAVE_ROOM,
  CHAT_SET_TYPING,
  CHAT_ACTIVE_TAB,
  CHAT_RESET,
  CHAT_CREATE_REQUEST_REQUEST,
  CHAT_CREATE_REQUEST_SUCCESS,
  CHAT_CREATE_REQUEST_FAIL,
  CHAT_ACCEPT_REQUEST_SUCCESS,
  CHAT_REJECT_REQUEST_SUCCESS,
  CHAT_MARK_SEEN_SUCCESS,
  CHAT_PIN_MESSAGE_SUCCESS,
  CHAT_UNPIN_MESSAGE_SUCCESS,
  CHAT_SEARCH_USERS_REQUEST,
  CHAT_SEARCH_USERS_SUCCESS,
  CHAT_SEARCH_USERS_FAIL,
  CHAT_SEARCH_USERS_CLEAR,
  CHAT_CALL_INCOMING,
  CHAT_CALL_ACCEPTED,
  CHAT_CALL_REJECTED,
  CHAT_CALL_CANCELED,
  CHAT_CALL_DISMISS,
  CHAT_CALL_OUTGOING_STATUS,
  CHAT_ACTIVE_CALL_SET,
  CHAT_ACTIVE_CALL_CLEAR,
  CHAT_CALL_OUTGOING_TYPE,
  CHAT_PRESENCE_UPDATE,
  CHAT_PRESENCE_MERGE,
  CHAT_LAST_ONLINE_UPDATE,
  CHAT_SET_UNREAD_COUNTS,
  CHAT_BULK_MARK,
  CHAT_BULK_MUTE,
  CHAT_BULK_REMOVE,
  CHAT_LOAD_OLDER_MESSAGES_REQUEST,
  CHAT_LOAD_OLDER_MESSAGES_SUCCESS,
  CHAT_LOAD_OLDER_MESSAGES_FAIL,
} from "./userChatTypes";

import { API } from "src/Funcs/APIFuncs/APIFunc";

const NEW_MESSAGE_SOUND_PATH = "/Sounds/NewMessage.wav";

/** Peer user id in a 1:1 chat (the other participant). */
export function getChatPeerUserId(chat, currentUserId) {
  if (!chat || !currentUserId) return null;
  const reqId = chat.requester?._id ?? chat.requester;
  const recId = chat.receiver?._id ?? chat.receiver;
  if (String(reqId) === String(currentUserId)) {
    return recId != null ? String(recId) : null;
  }
  return reqId != null ? String(reqId) : null;
}

function collectPeerIdsFromChats(items, currentUserId) {
  const ids = new Set();
  for (const chat of items || []) {
    const peer = getChatPeerUserId(chat, currentUserId);
    if (peer) ids.add(peer);
  }
  return [...ids];
}

async function fetchPresenceBatch(dispatch, userIds) {
  const ids = [...new Set(userIds)].filter(Boolean);
  if (!ids.length) return;
  try {
    const { data } = await axiosInstance.post(
      `${API}userChats/presence/batch`,
      { ids },
    );
    const body = data?.body ?? data;
    if (body && typeof body === "object" && !Array.isArray(body)) {
      dispatch({ type: CHAT_PRESENCE_MERGE, payload: body });
    }
  } catch {
    /* offline or unauthorized */
  }
}

/** Subscribe to site-wide presence for these user ids (accepted-chat peers only on server). */
export function subscribePresenceForUserIds(userIds) {
  const ids = [...new Set(userIds)].filter(Boolean);
  if (!ids.length) return;
  emitWhenConnected("presence:subscribe", { userIds: ids }).catch(() => {});
}

export const mergeChatPresenceFromServer =
  (userIds) => async (dispatch) => {
    await fetchPresenceBatch(dispatch, userIds);
  };

/** Full URL for sound file so it works regardless of app base path (e.g. under /Dashboard). */
function getSoundUrl() {
  if (typeof window === "undefined" || !window.location?.origin)
    return NEW_MESSAGE_SOUND_PATH;
  return `${window.location.origin}${NEW_MESSAGE_SOUND_PATH}`;
}

/** True if this tab is the visible/active one. Avoid playing sounds or showing toasts in background tabs. */
function isTabVisible() {
  if (typeof document === "undefined") return true;
  return document.visibilityState === "visible" && !document.hidden;
}

let newMessageAudio = null;
/** Shared ringtone Audio; must be unlocked by calling play() in the same user gesture as newMessageAudio. */
let sharedRingtoneAudio = null;
let audioUnlocked = false;

/** Incoming call ringtone (looping). Uses the same Audio element we unlocked on first gesture so browser allows play(). */
let incomingCallRingtoneKey = null;

function startIncomingCallRingtone(chatId, roomName) {
  if (typeof window === "undefined" || !isTabVisible()) return;
  const key = chatId && roomName ? `${chatId}-${roomName}` : null;
  if (key && incomingCallRingtoneKey === key) return;
  stopIncomingCallRingtone();
  incomingCallRingtoneKey = key || undefined;
  unlockAudio();
  if (!sharedRingtoneAudio) return;
  try {
    sharedRingtoneAudio.src = getSoundUrl();
    sharedRingtoneAudio.volume = 0.7;
    sharedRingtoneAudio.loop = true;
    sharedRingtoneAudio.currentTime = 0;
    const playWhenReady = () => {
      if (incomingCallRingtoneKey !== null && sharedRingtoneAudio) {
        sharedRingtoneAudio.play().catch(() => {});
      }
    };
    if (sharedRingtoneAudio.readyState >= 2) {
      playWhenReady();
    } else {
      sharedRingtoneAudio.addEventListener("canplaythrough", playWhenReady, {
        once: true,
      });
      sharedRingtoneAudio.addEventListener("error", () => {}, { once: true });
    }
  } catch {
    // ignore
  }
}

function stopIncomingCallRingtone() {
  incomingCallRingtoneKey = null;
  if (sharedRingtoneAudio) {
    try {
      sharedRingtoneAudio.pause();
      sharedRingtoneAudio.currentTime = 0;
    } catch {
      // ignore
    }
  }
}

function ensureChatAudioElementsReady() {
  if (newMessageAudio && sharedRingtoneAudio) return;
  const url = getSoundUrl();
  try {
    if (!newMessageAudio) {
      newMessageAudio = new Audio(url);
      newMessageAudio.volume = 0.6;
      newMessageAudio.preload = "auto";
    }
    if (!sharedRingtoneAudio) {
      sharedRingtoneAudio = new Audio(url);
      sharedRingtoneAudio.volume = 0.7;
      sharedRingtoneAudio.preload = "auto";
    }
  } catch {
    // ignore
  }
}

/**
 * Unlock audio for programmatic play. Must run inside a user gesture (click/keydown/touch).
 * Both elements must get play() in the same synchronous call stack as the gesture so the browser allows it.
 * Returns true if already unlocked, or a Promise that resolves to true/false when unlock attempt finishes.
 */
function unlockAudio() {
  if (typeof window === "undefined") return true;
  if (audioUnlocked) return true;

  ensureChatAudioElementsReady();
  if (!newMessageAudio || !sharedRingtoneAudio) return false;

  const prevMsgVolume =
    typeof newMessageAudio.volume === "number" ? newMessageAudio.volume : 0.6;
  const prevRingVolume =
    typeof sharedRingtoneAudio.volume === "number"
      ? sharedRingtoneAudio.volume
      : 0.7;
  newMessageAudio.volume = 0;
  sharedRingtoneAudio.volume = 0;

  const p1 = newMessageAudio.play();
  const p2 = sharedRingtoneAudio.play();

  const onDone = (success) => {
    try {
      newMessageAudio.pause();
      newMessageAudio.currentTime = 0;
      newMessageAudio.volume = prevMsgVolume;
      sharedRingtoneAudio.pause();
      sharedRingtoneAudio.currentTime = 0;
      sharedRingtoneAudio.volume = prevRingVolume;
    } catch {
      // ignore
    }
    if (success) audioUnlocked = true;
  };

  if (p1 && typeof p1.then === "function") {
    return p1
      .then(() => {
        onDone(true);
        return true;
      })
      .catch(() => {
        onDone(false);
        return false;
      });
  }
  audioUnlocked = true;
  onDone(true);
  return true;
}

function ensureNewMessageAudioReady() {
  ensureChatAudioElementsReady();
}

/** Max age (ms) for a message to be considered "new" for playing sound. Avoids playing when opening a chat that had recent messages or when server replays history. */
const NEW_MESSAGE_SOUND_MAX_AGE_MS = 8000;

/** Play new message sound (e.g. when message arrives and user is not on that chat). Only in visible tab. */
function playNewMessageSound() {
  if (typeof window === "undefined" || !isTabVisible()) return;
  ensureNewMessageAudioReady();
  if (!newMessageAudio) return;
  try {
    newMessageAudio.currentTime = 0;
    newMessageAudio.play().catch(() => {});
  } catch {
    // ignore
  }
}

/** Returns true if the message is recent enough to justify playing the new-message sound (avoids playing when opening a chat or when server sends old messages). */
function isMessageRecentForSound(message) {
  if (!message) return false;
  const createdAt = message.CreatedAt ?? message.createdAt;
  if (!createdAt) return true;
  const ts =
    typeof createdAt === "number" ? createdAt : new Date(createdAt).getTime();
  if (Number.isNaN(ts)) return true;
  return Date.now() - ts <= NEW_MESSAGE_SOUND_MAX_AGE_MS;
}

/** Returns true if the message is a call-related system message (e.g. "Call declined."). We do not show toast or play sound for these. */
function isCallSystemMessage(message) {
  if (!message) return false;
  const text = (message?.Text ?? message?.text ?? "").trim();
  const callSystemPhrases = [
    "Call declined",
    "Call accepted",
    "Call canceled",
    "Call ended",
    "A call was connected",
  ];
  return callSystemPhrases.some(
    (phrase) => text.startsWith(phrase) || text === phrase + "." || text === phrase,
  );
}

if (typeof window !== "undefined") {
  ensureChatAudioElementsReady();

  const capture = true;
  const removeUnlockListeners = () => {
    document.removeEventListener("click", unlockOnInteraction, capture);
    document.removeEventListener("keydown", unlockOnInteraction, capture);
    document.removeEventListener("touchstart", unlockOnInteraction, capture);
  };
  const unlockOnInteraction = () => {
    const result = unlockAudio();
    const onUnlockDone = (success) => {
      if (success) removeUnlockListeners();
    };
    if (result && typeof result.then === "function") {
      result.then(onUnlockDone);
    } else {
      onUnlockDone(!!result);
    }
  };
  document.addEventListener("click", unlockOnInteraction, {
    once: true,
    capture,
  });
  document.addEventListener("keydown", unlockOnInteraction, {
    once: true,
    capture,
  });
  document.addEventListener("touchstart", unlockOnInteraction, {
    once: true,
    capture,
  });
}

/**
 * Upload a file for the given chat. File is stored in AWS at user/:userId/chats/:chatId/
 * @param {string} chatId
 * @param {File} file
 * @returns {Promise<string>} public URL (path or full URL) for the uploaded file
 */
export const uploadChatFile = (chatId, file) => async () => {
  const formData = new FormData();
  formData.append("file", file);
  const url = `${API}userChats/${String(chatId)}/upload`;
  const guard = createUploadStallGuard();
  try {
    const { data } = await axiosInstance.post(url, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      signal: guard.signal,
    });
    const body = data?.body ?? data;
    const uploadedUrl = body?.url;
    if (!uploadedUrl) throw new Error("No url in upload response");
    return uploadedUrl;
  } finally {
    guard.done();
  }
};

let chatListenersUnregister = null;
let groupListenersUnregister = null;

/**
 * Initialize chat socket and register listeners globally (idempotent).
 * Call on app load when user is logged in, and after login so messages/sound work from any page.
 */
export function initChatSocket() {
  if (typeof window === "undefined") return;
  if (!localStorage.getItem("UserID")) return;
  if (chatListenersUnregister) return;
  getSocketUserChat();
  chatListenersUnregister = registerChatSocketListeners(
    store.dispatch,
    store.getState,
  );
  groupListenersUnregister = registerGroupSocketListeners(
    store.dispatch,
    store.getState,
  );
}

/**
 * Unregister chat listeners and disconnect (e.g. on logout). Call after clearing UserID.
 */
export function teardownChatSocket() {
  if (chatListenersUnregister) {
    chatListenersUnregister();
    chatListenersUnregister = null;
  }
  if (groupListenersUnregister) {
    groupListenersUnregister();
    groupListenersUnregister = null;
  }
  disconnectSocketUserChat();
}

/**
 * Register socket listeners for real-time chat. Call once when Chat page mounts or via initChatSocket.
 * On reconnect, re-joins the current chat room if one is selected.
 * @param {Function} dispatch
 * @param {Function} [getState] - optional; if provided, used on connect to re-join active chat room
 * @returns {Function} unsubscribe
 */
export const registerChatSocketListeners = (dispatch, getState) => {
  const socket = getSocketUserChat();
  if (!socket) return () => {};

  const onNewMessage = (payload) => {

   

    const isOnChatPage =
      typeof window !== "undefined" && window.location?.pathname === "/chat";
    const state = typeof getState === "function" ? getState() : {};
    const activeId = state?.userChat?.activeChatId;


    const isForActiveChat =
      isOnChatPage &&
      activeId != null &&
      String(payload?.chatId) === String(activeId);


    const message = payload?.message;
    const isCallSystem = isCallSystemMessage(message);
    const shouldPlaySound =
      payload?.chatId &&
      !isForActiveChat &&
      isMessageRecentForSound(message) &&
      !isCallSystem;



    // const shouldNotifyInThisTab = shouldPlaySound && isTabVisible();
    const shouldNotifyInThisTab = false;


    if (shouldNotifyInThisTab) {
      playNewMessageSound();
      // Also show a toast notification with sender and message preview (only in visible tab).
      const senderName =
        message?.SenderName ||
        message?.senderName ||
        message?.Sender?.Name ||
        message?.Sender?.FullName ||
        "New message";
      const textPreview = (message?.Text || message?.text || "").slice(0, 80);
      const title = senderName;
      const body =
        textPreview && textPreview.length > 0 ? textPreview : "[Attachment]";

      // Build chat URL using the same pattern as RequestPartnerModal / BookServiceModal.
      const chatId = payload?.chatId ? String(payload.chatId) : null;
      const url = chatId ? `/chat?chat=${chatId}` : null;

      // Try to infer chat type/name from state (direct chat name, etc.)
      const chats = state?.userChat?.items || [];
      const chat = chats.find((c) => String(c._id) === String(payload?.chatId));
      const chatName = chat?.Name;
      const subtitle = chatName ? `in ${chatName}` : "in direct chat";
       const isMuted = chat?.Muted === true || chat?.IsMuted === true;

      

      if (!isMuted) {
        toast.info(
          React.createElement(ChatToast, {
            title,
            subtitle,
            body,
            url,
            onOpenChat: getToastNavigate(),
          }),
          {
            autoClose: 8000,
            closeOnClick: true,
          },
        );
      }
    }



    dispatch({ type: CHAT_NEW_MESSAGE, payload });
  };
  const onNewChatRequest = (payload) => {
    dispatch({ type: CHAT_NEW_CHAT_REQUEST, payload });
  };
  const onRequestAccepted = (payload) => {
    dispatch({ type: CHAT_REQUEST_ACCEPTED, payload });
  };
  const onRequestRejected = (payload) => {
    dispatch({ type: CHAT_REQUEST_REJECTED, payload });
  };

  const onPresenceUpdate = (payload) => {
    if (payload?.userId != null) {
      dispatch({
        type: CHAT_PRESENCE_UPDATE,
        payload: {
          userId: payload.userId,
          online: payload.online === true,
          lastOnlineAt: payload.lastOnlineAt ?? null,
        },
      });
    }
  };

  const onConnect = () => {
    if (typeof getState === "function") {
      const state = getState();
      const activeId = state?.userChat?.activeChatId;
      if (activeId) {
        socket.emit("chat:joinRoom", { chatId: String(activeId) });
      }
      const uid =
        typeof window !== "undefined"
          ? localStorage.getItem("UserID")
          : null;
      const items = state?.userChat?.items || [];
      const peerIds = collectPeerIdsFromChats(items, uid);
      if (peerIds.length) {
        socket.emit("presence:subscribe", { userIds: peerIds });
      }
      socket.emit("chat:getUnreadCount", {}, (payload) => {
        if (payload?.status) {
          dispatch({
            type: CHAT_SET_UNREAD_COUNTS,
            payload: payload.byChatId || {},
          });
        }
      });
      socket.emit("group:getUnreadCount", {}, (payload) => {
        if (payload?.status) {
          dispatch({
            type: "GROUP_SET_UNREAD_COUNTS",
            payload: payload.byGroupId || {},
          });
        }
      });
    }
  };

  const onMessageEdited = (payload) => {
    if (
      payload?.chatId &&
      payload?.messageId != null &&
      payload?.text != null
    ) {
      dispatch({
        type: CHAT_EDIT_MESSAGE_SUCCESS,
        payload: {
          body: {
            chatId: payload.chatId,
            messageId: payload.messageId,
            text: payload.text,
          },
        },
      });
    }
  };
  const onMessageDeleted = (payload) => {
    if (payload?.chatId && payload?.messageId != null) {
      dispatch({
        type: CHAT_DELETE_MESSAGE_SUCCESS,
        payload: {
          body: {
            chatId: payload.chatId,
            messageId: payload.messageId,
            hard: payload.hard === true,
          },
        },
      });
    }
  };
  const onMessagesBulkDeleted = (payload) => {
    if (payload?.chatId && payload?.messageIds?.length) {
      dispatch({
        type: CHAT_BULK_DELETE_MESSAGES_SUCCESS,
        payload: {
          body: {
            chatId: payload.chatId,
            deletedIds: payload.messageIds,
            hard: payload.hard === true,
          },
        },
      });
    }
  };
  const onMessageLiked = (payload) => {
    if (payload?.chatId && payload?.messageId != null && payload?.userId) {
      dispatch({ type: CHAT_MESSAGE_LIKED, payload });
    }
  };
  const onMessageReaction = (payload) => {
    if (payload?.chatId && payload?.messageId != null) {
      dispatch({ type: CHAT_MESSAGE_REACTION, payload });
    }
  };
  const onMessagePinned = (payload) => {
    if (payload?.chatId && payload?.PinnedMessageIds) {
      dispatch({
        type: CHAT_PIN_MESSAGE_SUCCESS,
        payload: {
          body: {
            chatId: payload.chatId,
            messageId: payload.messageId,
            PinnedMessageIds: payload.PinnedMessageIds,
          },
        },
      });
    }
  };
  const onMessageUnpinned = (payload) => {
    if (payload?.chatId && payload?.PinnedMessageIds) {
      dispatch({
        type: CHAT_UNPIN_MESSAGE_SUCCESS,
        payload: {
          body: {
            chatId: payload.chatId,
            messageId: payload.messageId,
            PinnedMessageIds: payload.PinnedMessageIds,
          },
        },
      });
    }
  };
  const onSeen = (payload) => {
    if (payload?.chatId && payload?.userId != null) {
      dispatch({ type: CHAT_REMOTE_SEEN, payload });
    }
  };

  const onCallIncoming = (payload) => {
    if (!payload?.chatId || !payload?.roomName) return;
    dispatch({ type: CHAT_CALL_INCOMING, payload });
    startIncomingCallRingtone(payload.chatId, payload.roomName);
  };
  const onCallAccepted = (payload) => {
    stopIncomingCallRingtone();
    if (payload?.chatId) {
      const state = typeof getState === "function" ? getState() : {};
      const chatState = state?.userChat ?? {};
      if (
        chatState.outgoingCallStatus === "ringing" &&
        String(chatState.activeChatId) === String(payload.chatId)
      ) {
        const roomName = payload.roomName || `chat-${payload.chatId}`;
        const callType = chatState.outgoingCallType || "audio";
        dispatch({
          type: CHAT_ACTIVE_CALL_SET,
          payload: { chatId: payload.chatId, roomName, callType },
        });
      }
      dispatch({ type: CHAT_CALL_ACCEPTED, payload });
    }
  };
  const onCallRejected = (payload) => {
    stopIncomingCallRingtone();
    if (payload?.chatId) {
      dispatch({ type: CHAT_CALL_REJECTED, payload });
    }
  };
  const onCallCanceled = (payload) => {
    stopIncomingCallRingtone();
    if (payload?.chatId) {
      dispatch({ type: CHAT_CALL_CANCELED, payload });
    }
  };

  socket.on("chat:newMessage", onNewMessage);
  socket.on("chat:newChatRequest", onNewChatRequest);
  socket.on("chat:requestAccepted", onRequestAccepted);
  socket.on("chat:requestRejected", onRequestRejected);
  socket.on("chat:messageEdited", onMessageEdited);
  socket.on("chat:messageDeleted", onMessageDeleted);
  socket.on("chat:bulkMessagesDeleted", onMessagesBulkDeleted);
  socket.on("chat:messageLiked", onMessageLiked);
  socket.on("chat:messageReaction", onMessageReaction);
  socket.on("chat:messagePinned", onMessagePinned);
  socket.on("chat:messageUnpinned", onMessageUnpinned);
  socket.on("chat:seen", onSeen);
  socket.on("unread:chat", (payload) => {
    if (payload?.byChatId) {
      dispatch({
        type: CHAT_SET_UNREAD_COUNTS,
        payload: payload.byChatId,
      });
    }
  });
  socket.on("chat:callIncoming", onCallIncoming);
  socket.on("chat:callAccepted", onCallAccepted);
  socket.on("chat:callRejected", onCallRejected);
  socket.on("chat:callCanceled", onCallCanceled);
  socket.on("presence:update", onPresenceUpdate);
  socket.on("connect", onConnect);
  if (socket.connected) onConnect();

  const unregister = () => {
    socket.off("chat:newMessage", onNewMessage);
    socket.off("chat:newChatRequest", onNewChatRequest);
    socket.off("chat:requestAccepted", onRequestAccepted);
    socket.off("chat:requestRejected", onRequestRejected);
    socket.off("chat:messageEdited", onMessageEdited);
    socket.off("chat:messageDeleted", onMessageDeleted);
    socket.off("chat:bulkMessagesDeleted", onMessagesBulkDeleted);
    socket.off("chat:messageLiked", onMessageLiked);
    socket.off("chat:messageReaction", onMessageReaction);
    socket.off("chat:messagePinned", onMessagePinned);
    socket.off("chat:messageUnpinned", onMessageUnpinned);
    socket.off("chat:seen", onSeen);
    socket.off("unread:chat");
    socket.off("chat:callIncoming", onCallIncoming);
    socket.off("chat:callAccepted", onCallAccepted);
    socket.off("chat:callRejected", onCallRejected);
    socket.off("chat:callCanceled", onCallCanceled);
    socket.off("presence:update", onPresenceUpdate);
    socket.off("connect", onConnect);
  };
  return unregister;
};

export const getChatList =
  (query = {}) =>
  async (dispatch) => {
    dispatch({ type: CHAT_GET_USER_CHATS_REQUEST });
    try {
      const payload = await emitWithCallback("chat:getUserChats", { query });
      dispatch({ type: CHAT_GET_USER_CHATS_SUCCESS, payload });
      const items = payload?.body?.items ?? [];
      const uid =
        typeof window !== "undefined"
          ? localStorage.getItem("UserID")
          : null;
      const peerIds = collectPeerIdsFromChats(items, uid);
      subscribePresenceForUserIds(peerIds);
      await fetchPresenceBatch(dispatch, peerIds);
      items.forEach((chat) => {
        const chatId = chat?._id;
        if (chatId) {
          emitWhenConnected("chat:joinRoom", { chatId: String(chatId) }).catch(
            () => {},
          );
        }
      });
    } catch (err) {
      const message = err?.message ?? "Failed to load chats";
      dispatch({ type: CHAT_GET_USER_CHATS_FAIL, payload: message });
    }
  };

export const getChatById =
  (chatId, query = { page: 1, limit: 30 }) =>
  async (dispatch) => {
    dispatch({ type: CHAT_GET_CHAT_BY_ID_REQUEST });
    try {
      const payload = await emitWithCallback("chat:getChatById", {
        params: { chatId },
        query,
      });
      dispatch({ type: CHAT_GET_CHAT_BY_ID_SUCCESS, payload });
      const uid =
        typeof window !== "undefined"
          ? localStorage.getItem("UserID")
          : null;
      const chat = payload?.body?.chat;
      const peer = getChatPeerUserId(chat, uid);
      if (peer) {
        subscribePresenceForUserIds([peer]);
        await fetchPresenceBatch(dispatch, [peer]);
      }
    } catch (err) {
      const message = err?.message ?? "Failed to load chat";
      dispatch({ type: CHAT_GET_CHAT_BY_ID_FAIL, payload: message });
    }
  };

export const loadOlderChatMessages =
  (chatId, page = 2) =>
  async (dispatch) => {
    dispatch({ type: CHAT_LOAD_OLDER_MESSAGES_REQUEST });
    try {
      const payload = await emitWithCallback("chat:getChatById", {
        params: { chatId },
        query: { page, limit: 30 },
      });
      dispatch({
        type: CHAT_LOAD_OLDER_MESSAGES_SUCCESS,
        payload: { ...payload, _chatId: chatId },
      });
    } catch (err) {
      const message = err?.message ?? "Failed to load older messages";
      dispatch({ type: CHAT_LOAD_OLDER_MESSAGES_FAIL, payload: message });
    }
  };

export const sendMessage = (chatId, body) => async (dispatch) => {
  const tempId = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  const currentUserId = localStorage.getItem("UserID");

  let isVoiceUpload = body.voice === true && body._voiceBlob instanceof Blob;
  let voiceBlobToUse = isVoiceUpload ? body._voiceBlob : null;

  if (!isVoiceUpload && body.voice === true && body.attachments?.length > 0) {
    const firstUrl = body.attachments[0];
    if (typeof firstUrl === "string" && firstUrl.startsWith("blob:")) {
      try {
        const response = await fetch(firstUrl);
        voiceBlobToUse = await response.blob();
        isVoiceUpload = true;
      } catch {
        console.warn("[sendMessage] failed to fetch blob from object URL");
      }
    }
  }

  const tempMessage = {
    _id: tempId,
    Text: body.text ?? "",
    MessageBody: body.text ?? "",
    Attachments: body.attachments ?? [],
    MessageAttachs: body.attachments ?? [],
    Sender: currentUserId,
    CreatedAt: Date.now(),
    Time: Date.now(),
    _status: isVoiceUpload ? "uploading" : "sending",
  };
  if (body.replyTo) {
    tempMessage.ReplyTo = body.replyTo;
    tempMessage.replyTo = body.replyTo;
  }
  dispatch({
    type: CHAT_SEND_MESSAGE_REQUEST,
    payload: { tempMessage, chatId },
  });

  if (isVoiceUpload) {
    try {
      const voiceFile = new File([voiceBlobToUse], "voice.webm", {
        type: voiceBlobToUse.type || "audio/webm",
      });
      const remoteUrl = await uploadChatFile(chatId, voiceFile)();
      dispatch({
        type: CHAT_VOICE_UPLOAD_COMPLETE,
        payload: { tempId, remoteUrl, chatId },
      });
      const payload = await emitWithCallback(
        "chat:sendMessage",
        {
          params: { chatId },
          body: {
            text: body.text || "",
            attachments: [remoteUrl],
            ...(body.replyTo ? { replyTo: body.replyTo } : {}),
          },
        },
        { retryOnTimeout: true },
      );
      dispatch({
        type: CHAT_SEND_MESSAGE_SUCCESS,
        payload: { ...payload, _optimisticTempId: tempId },
      });
    } catch (err) {
      const message = err?.message ?? "Failed to send voice message";
      console.error(
        "[sendMessage] voice send failed",
        { chatId, tempId, error: message },
        err,
      );
      dispatch({
        type: CHAT_SEND_MESSAGE_FAIL,
        payload: { error: message, tempId },
      });
    }
    return;
  }

  try {
    const payload = await emitWithCallback(
      "chat:sendMessage",
      {
        params: { chatId },
        body,
      },
      { retryOnTimeout: true },
    );
    dispatch({
      type: CHAT_SEND_MESSAGE_SUCCESS,
      payload: { ...payload, _optimisticTempId: tempId },
    });
  } catch (err) {
    const message = err?.message ?? "Failed to send message";
    console.error(
      "[sendMessage] failed",
      { chatId, tempId, body: body ?? {}, error: message },
      err,
    );
    dispatch({
      type: CHAT_SEND_MESSAGE_FAIL,
      payload: { error: message, tempId },
    });
  }
};

export const editMessage = (chatId, messageId, text) => async (dispatch) => {
  const payload = await emitWithCallback("chat:editMessage", {
    params: { chatId, messageId },
    body: { text },
  });
  dispatch({
    type: CHAT_EDIT_MESSAGE_SUCCESS,
    payload: { ...payload, body: { ...payload?.body, text } },
  });
};

export const deleteMessage =
  (chatId, messageId, hard = false) =>
  async (dispatch) => {
    const payload = await emitWithCallback("chat:deleteMessage", {
      params: { chatId, messageId },
      query: hard ? { hard: "true" } : {},
    });
    dispatch({ type: CHAT_DELETE_MESSAGE_SUCCESS, payload });
  };

export const bulkDeleteMessages =
  (chatId, messageIds, hard = false) =>
  async (dispatch) => {
    const ids = (messageIds ?? []).map(String);
    const payload = await emitWithCallback(
      "chat:bulkDeleteMessages",
      {
        params: { chatId },
        body: { messageIds: ids },
        query: hard ? { hard: "true" } : {},
      },
      { retryOnTimeout: true },
    );
    // The server ack may not echo chatId/deletedIds. Normalize the payload so
    // the reducer always has what it needs to remove the messages locally.
    const body = payload?.body ?? {};
    dispatch({
      type: CHAT_BULK_DELETE_MESSAGES_SUCCESS,
      payload: {
        ...payload,
        body: {
          ...body,
          chatId: body.chatId ?? String(chatId),
          deletedIds:
            Array.isArray(body.deletedIds) && body.deletedIds.length
              ? body.deletedIds
              : ids,
          hard: body.hard ?? hard,
        },
      },
    });
    return payload;
  };

export const selectChat = (chatId) => (dispatch) => {
  dispatch({ type: CHAT_SELECT_CHAT, payload: chatId });
};

export const joinChatRoom = (chatId) => () => {
  const id = chatId != null ? String(chatId) : null;
  if (!id) return;
  emitWhenConnected("chat:joinRoom", { chatId: id }).catch(() => {});
};

export const leaveChatRoom = (chatId) => (dispatch) => {
  const socket = getSocketUserChat();
  const id = chatId != null ? String(chatId) : null;
  if (socket && id) socket.emit("chat:leaveRoom", { chatId: id });
  dispatch({ type: CHAT_LEAVE_ROOM, payload: id });
};

export const dismissIncomingCall = () => (dispatch) => {
  stopIncomingCallRingtone();
  dispatch({ type: CHAT_CALL_DISMISS });
};

export const setOutgoingCallStatus = (status) => (dispatch) => {
  dispatch({ type: CHAT_CALL_OUTGOING_STATUS, payload: status });
};

export const startChatCall =
  (chatId, callType = "audio", callerName) =>
  (dispatch) => {
    const roomName = `chat-${chatId}`;
    emitWhenConnected("chat:call:start", {
      chatId: String(chatId),
      callType,
      roomName,
      callerName,
    }).catch(() => {});
    dispatch({ type: CHAT_CALL_OUTGOING_STATUS, payload: "ringing" });
    dispatch({ type: CHAT_CALL_OUTGOING_TYPE, payload: callType });
  };

export const acceptChatCall = (chatId, roomName) => (dispatch) => {
  stopIncomingCallRingtone();
  emitWhenConnected("chat:call:accept", {
    chatId: String(chatId),
    roomName: roomName || `chat-${chatId}`,
  }).catch(() => {});
  dispatch({ type: CHAT_CALL_DISMISS });
};

export const rejectChatCall = (chatId) => (dispatch) => {
  stopIncomingCallRingtone();
  emitWhenConnected("chat:call:reject", { chatId: String(chatId) }).catch(
    () => {},
  );
  dispatch({ type: CHAT_CALL_DISMISS });
};

export const cancelChatCall = (chatId) => (dispatch) => {
  emitWhenConnected("chat:call:cancel", { chatId: String(chatId) }).catch(
    () => {},
  );
  dispatch({ type: CHAT_CALL_OUTGOING_STATUS, payload: null });
};

export const setActiveChatCall = (payload) => (dispatch) => {
  dispatch({ type: CHAT_ACTIVE_CALL_SET, payload });
};

export const clearActiveChatCall = () => (dispatch) => {
  dispatch({ type: CHAT_ACTIVE_CALL_CLEAR });
};

export const setTyping = (userId) => (dispatch) => {
  dispatch({ type: CHAT_SET_TYPING, payload: userId });
};

export const setActiveTab = (tab) => (dispatch) => {
  dispatch({ type: CHAT_ACTIVE_TAB, payload: tab });
};

export const resetChat = () => (dispatch) => {
  dispatch({ type: CHAT_RESET });
  disconnectSocketUserChat();
};

export const createChatRequest = (body) => async (dispatch) => {
  dispatch({ type: CHAT_CREATE_REQUEST_REQUEST });
  try {
    const payload = await emitWithCallback("chat:createChatRequest", { body });
    dispatch({ type: CHAT_CREATE_REQUEST_SUCCESS, payload });
    return payload;
  } catch (err) {
    const message = err?.message ?? "Failed to create chat";
    dispatch({ type: CHAT_CREATE_REQUEST_FAIL, payload: message });
    throw err;
  }
};

export const acceptChatRequest = (chatId) => async (dispatch) => {
  try {
    const payload = await emitWithCallback("chat:acceptChatRequest", {
      params: { chatId },
    });
    dispatch({ type: CHAT_ACCEPT_REQUEST_SUCCESS, payload });
  } catch (err) {
    console.log(err);
    throw err;
  }
};

export const rejectChatRequest = (chatId) => async (dispatch) => {
  try {
    const payload = await emitWithCallback("chat:rejectChatRequest", {
      params: { chatId },
    });
    dispatch({ type: CHAT_REJECT_REQUEST_SUCCESS, payload });
  } catch (err) {
    console.log(err);
    throw err;
  }
};

export const pinMessage = (chatId, messageId) => async (dispatch) => {
  const payload = await emitWithCallback("chat:pinMessage", {
    params: { chatId, messageId },
  });
  dispatch({ type: CHAT_PIN_MESSAGE_SUCCESS, payload });
};

export const unpinMessage = (chatId, messageId) => async (dispatch) => {
  const payload = await emitWithCallback("chat:unpinMessage", {
    params: { chatId, messageId },
  });
  dispatch({ type: CHAT_UNPIN_MESSAGE_SUCCESS, payload });
};

export const reactMessage =
  (chatId, messageId, emoji) => async (dispatch) => {
    const payload = await emitWithCallback("chat:toggleReaction", {
      params: { chatId, messageId },
      body: { emoji },
    });
    if (payload?.status && payload?.body) {
      dispatch({ type: CHAT_MESSAGE_REACTION, payload: payload.body });
    }
  };

export const markSeen =
  (chatId, messageIds = null) =>
  async (dispatch) => {
    try {
      await emitWithCallback("chat:markSeen", {
        params: { chatId },
        body: messageIds ? { messageIds } : {},
      });
      dispatch({ type: CHAT_MARK_SEEN_SUCCESS });
    } catch (_) {
      // optional
    }
  };

export const markChatsReadBulk =
  (chatIds, read = true) =>
  async (dispatch) => {
    if (!chatIds?.length) return;
    dispatch({ type: CHAT_BULK_MARK, payload: { chatIds, read } });
    await axiosInstance.post(
      `${API}userChats/bulk/${read ? "markSeen" : "markUnread"}`,
      { chatIds },
    );
  };

export const fetchArchivedChats = () => async () => {
  const payload = await emitWithCallback("chat:getUserChats", {
    query: { archive: "true", page: 1, limit: 100 },
  });
  return payload?.body?.items ?? [];
};

export const markAllChatsRead = () => async () => {
  await axiosInstance.post(`${API}userChats/bulk/markSeen`, { all: true });
};

export const muteChatsBulk =
  (chatIds, mute = true) =>
  async (dispatch) => {
    if (!chatIds?.length) return;
    dispatch({ type: CHAT_BULK_MUTE, payload: { chatIds, mute } });
    await axiosInstance.post(`${API}userChats/bulk/mute`, { chatIds, mute });
  };

export const archiveChatsBulk =
  (chatIds, archive = true) =>
  async (dispatch) => {
    if (!chatIds?.length) return;
    dispatch({ type: CHAT_BULK_REMOVE, payload: { chatIds } });
    await axiosInstance.post(`${API}userChats/bulk/archive`, {
      chatIds,
      archive,
    });
  };

export const deleteChatsBulk = (chatIds) => async (dispatch) => {
  if (!chatIds?.length) return;
  dispatch({ type: CHAT_BULK_REMOVE, payload: { chatIds } });
  await axiosInstance.post(`${API}userChats/bulk/delete`, { chatIds });
};

export const searchUsers =
  (q, limit = 20) =>
  async (dispatch) => {
    if (!q || String(q).trim().length < 2) {
      dispatch({ type: CHAT_SEARCH_USERS_CLEAR });
      return;
    }
    dispatch({ type: CHAT_SEARCH_USERS_REQUEST });
    try {
      const payload = await emitWithCallback("chat:searchUsers", {
        query: { q: String(q).trim(), limit },
      });
      dispatch({ type: CHAT_SEARCH_USERS_SUCCESS, payload });
    } catch (err) {
      dispatch({
        type: CHAT_SEARCH_USERS_FAIL,
        payload: err?.message ?? "Search failed",
      });
    }
  };

export const clearSearchUsers = () => (dispatch) => {
  dispatch({ type: CHAT_SEARCH_USERS_CLEAR });
};
